import React, { useEffect, useState } from "react";
import {
  FiArrowUpRight, FiGithub, FiLinkedin, FiMail, FiMoon, FiSun,
  FiDownload, FiMapPin, FiExternalLink, FiTerminal, FiServer, FiZap, FiShield, FiGitBranch, FiLayers, FiMonitor, FiCode, FiDatabase, FiPackage
} from "react-icons/fi";
import { FaTelegramPlane, FaJava, FaAws, FaReact, FaAngular, FaNodeJs, FaGitAlt } from "react-icons/fa";
import "./index.css";

import profile from "./assets/profile.png";
import aboutVideo from "./assets/about.mp4";
import vitpyq from "./assets/vitpyq.png";
import resume from "./assets/vikasheresume.pdf";
import recommendationLetter from "./assets/lr.pdf";
import ace from "./assets/ace.png";
import jobsearch from "./assets/jobsearch.png";
import gym from "./assets/gym.png";
import police from "./assets/police.png";
import nptel from "./assets/nptel.png";

const projects = [
  { title: "Bulk Email Marketing", type: "Full-stack product", desc: "A web application for building and managing bulk email campaigns with a focused, conversion-friendly workflow.", image: "https://user-images.githubusercontent.com/72156896/249348558-9aea678b-b3ff-46fc-833c-dfb3aaa5a6f7.png", tags: ["React", "Node.js", "Web"], demo: "https://email-marketing-software.vercel.app/", github: "https://github.com/vikashchand/Email-Marketing-software", featured: true },
  { title: "Intelligent Question Paper Archive", type: "Search & archive", desc: "An intelligent archive for discovering and organizing university question papers with a clean search experience.", image: vitpyq, tags: ["React", "MongoDB", "Search"], demo: "https://20mis.vercel.app/", github: "https://github.com/vikashchand/VITPYQ", featured: true },
  { title: "Ace Interview", type: "Interview platform", desc: "A focused interview-practice experience designed to make preparation simple, fast and repeatable.", image: ace, tags: ["React", "UI/UX"], demo: "https://ace-interview.vercel.app/", github: "https://github.com/vikashchand/Ace-Interview" },
  { title: "Police Portal", type: "Web application", desc: "A practical portal interface built around structured information and straightforward user flows.", image: police, tags: ["React", "Web"], demo: "https://uppolice.vercel.app/", github: "https://github.com/vikashchand/Police-portal" },
  { title: "Maxx Fitness", type: "Brand website", desc: "A bold fitness website with a visual-first layout and responsive presentation.", image: gym, tags: ["React", "Design"], demo: "https://maxfitness.vercel.app/", github: "https://github.com/vikashchand/maxxfitness" },
  { title: "NPTEL Quiz", type: "Learning tool", desc: "A lightweight quiz experience built for fast practice and revision.", image: nptel, tags: ["React", "Education"], demo: "https://nptelquiz.vercel.app/", github: "https://github.com/vikashchand/nptel" },
  { title: "Job Search Mobile App", type: "Mobile application", desc: "A React-based mobile job discovery experience built around a simple application flow.", image: jobsearch, tags: ["React Native", "Mobile"], github: "https://github.com/vikashchand/jobsearch" },
];

const skills = [
  ["Java / Spring Boot", "Backend", FaJava],
  ["Microservices", "Backend", FiLayers],
  ["REST APIs", "Backend", FiServer],
  ["Node.js", "Backend", FaNodeJs],
  ["AWS Lambda", "Cloud", FaAws],
  ["S3 / CloudFront", "Cloud", FaAws],
  ["API Gateway", "Cloud", FiGitBranch],
  ["AWS", "Cloud", FaAws],
  ["Angular", "Frontend", FaAngular],
  ["React", "Frontend", FaReact],
  ["Electron.js", "Desktop", FiMonitor],
  ["JavaScript", "Frontend", FiCode],
  ["DynamoDB", "Database", FiDatabase],
  ["MongoDB", "Database", FiDatabase],
  ["MySQL", "Database", FiDatabase],
  ["SonarQube", "Quality", FiShield],
  ["Maven", "Build", FiPackage],
  ["Git / Bitbucket", "Workflow", FaGitAlt]
];

const engineeringHighlights = [
  {
    title: "File Conversion Automation",
    eyebrow: "SIGNIFY · INTERNSHIP",
    desc: "Automated a previously 5-step, 5-API file conversion workflow. Built the Spring Boot backend and React experience, then integrated AWS Lambda, API Gateway, S3 and CloudFront into a hosted pipeline — reducing the conversion time from roughly 3 minutes for a single file to about 40 seconds for 500 files.",
    tags: ["Spring Boot", "React", "AWS Lambda", "API Gateway", "S3", "CloudFront"],
    icon: FiZap
  },
  {
    title: "License Checker",
    eyebrow: "SIGNIFY · FULL-TIME",
    desc: "Developed an Electron.js tool to scan libraries, validate license usage across a large library set and license database, surface issues and make compliance checks dramatically easier.",
    tags: ["Electron.js", "JavaScript", "Automation", "License Compliance", "Performance"],
    icon: FiShield
  },
  {
    title: "Developer Productivity Extensions",
    eyebrow: "SIGNIFY · INTERNAL TOOLS",
    desc: "Created internal extensions and utilities that streamline developer workflows around company products, reducing repetitive steps and making day-to-day engineering work faster.",
    tags: ["Developer Tools", "Extensions", "Automation", "Productivity"],
    icon: FiGitBranch
  }
];

const techLogo = (name, src) => ({ name, src });

const timeline = [
  {
    year: "May 2025 — Present",
    role: "Assistant Development Engineer",
    company: "Signify",
    companyUrl: "https://www.linkedin.com/company/signifycompany/",
    companyLogo: "https://www.google.com/s2/favicons?domain=signify.com&sz=128",
    text: "Backend-leaning software engineering across Spring Boot, Angular, AWS and microservices. I work on APIs, cloud integrations and internal engineering tools, with hands-on exposure to Maven, SonarQube and production-oriented development.",
    tech: [
      techLogo("Spring Boot", "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/spring/spring-original.svg"),
      techLogo("Angular", "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/angularjs/angularjs-original.svg"),
      techLogo("AWS", "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/amazonwebservices/amazonwebservices-original-wordmark.svg")
    ]
  },
  {
    year: "Jul 2024 — May 2025",
    role: "Intern",
    company: "Signify",
    companyUrl: "https://www.linkedin.com/company/signifycompany/",
    companyLogo: "https://www.google.com/s2/favicons?domain=signify.com&sz=128",
    text: "Worked on automation and product engineering, including a file-conversion platform that replaced a 5-step workflow with a streamlined Spring Boot + React + AWS solution and reduced conversion time to about 40 seconds for 500 files.",
    tech: [
      techLogo("Spring Boot", "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/spring/spring-original.svg"),
      techLogo("AWS", "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/amazonwebservices/amazonwebservices-original-wordmark.svg"),
      techLogo("React", "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/react/react-original.svg")
    ]
  },
  {
    year: "May 2023 — Dec 2023",
    role: "Intern",
    company: "Valsco Technology",
    companyUrl: "https://www.linkedin.com/company/valscotech/",
    companyLogo: "https://www.google.com/s2/favicons?domain=valscotech.com&sz=128",
    text: "Worked on product and web development, contributing to the early development of Jurident and building frontend experiences for company products. This was my first professional internship before joining Signify.",
    tech: [
      techLogo("React", "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/react/react-original.svg")
    ],
    recommendation: "./assets/lr.pdf"
  }
];

function App() {
  const [dark, setDark] = useState(false);
  const [menu, setMenu] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [now, setNow] = useState(new Date());
  const [pointer, setPointer] = useState({ x: 50, y: 50 });

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const move = (e) => setPointer({ x: (e.clientX / window.innerWidth) * 100, y: (e.clientY / window.innerHeight) * 100 });
    window.addEventListener("pointermove", move);
    return () => window.removeEventListener("pointermove", move);
  }, []);

  useEffect(() => {
    const els = document.querySelectorAll(".animate-on-scroll");
    const observer = new IntersectionObserver(entries => entries.forEach(entry => { if (entry.isIntersecting) entry.target.classList.add("is-visible"); }), { threshold: .12 });
    els.forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const liveDate = now.toLocaleDateString("en-IN", { weekday: "long", day: "2-digit", month: "short", year: "numeric" });
  const liveTime = now.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true });

  useEffect(() => { document.documentElement.dataset.theme = dark ? "dark" : "light"; }, [dark]);

  const scrollTo = (id) => { setMenu(false); document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }); };

  return <div className="shell" style={{ "--mx": `${pointer.x}%`, "--my": `${pointer.y}%` }}>
    <div className="noise" />
    <div className="orb orb-one" /><div className="orb orb-two" />

    <header className={`topbar ${scrolled ? "is-scrolled" : ""}`}>
      <a className="brand" href="#home"><span>VC</span><b>Vikash<span>.</span></b></a>
      <nav className={menu ? "nav-open" : ""}>
        {["about", "experience", "skills", "projects", "contact"].map(x => <a key={x} href={`#${x}`} onClick={() => setMenu(false)}>{x}</a>)}
      </nav>
      <div className="top-actions">
        <button className="icon-btn" onClick={() => setDark(v => !v)} aria-label="Toggle theme">{dark ? <FiSun /> : <FiMoon />}</button>
        <a className="mini-cta" href="#contact">Let's talk <FiArrowUpRight /></a>
        <button className="menu-btn" onClick={() => setMenu(v => !v)} aria-label="Open menu"><span /><span /></button>
      </div>
    </header>

    <main>
      <section id="home" className="hero section-pad">
        <div className="hero-copy reveal">
          <div className="eyebrow"><span className="status-dot" /> Available for interesting work</div>
          <p className="hero-kicker">BACKEND · CLOUD · FULL-STACK ENGINEERING</p>
          <h1>Building digital<br /><em>experiences</em> that work.</h1>
          <p className="hero-desc">I'm Vikash — a software engineer focused on backend systems, cloud engineering and the interfaces that connect them. I like turning slow, repetitive workflows into fast, reliable products.</p>
          <div className="hero-actions">
            <a className="primary-btn" href="#projects">Explore my work <FiArrowUpRight /></a>
            <a
              className="ghost-btn"
              href={resume}
              download="Vikash-Chand-Resume.pdf"
            >
              <FiDownload />
              Resume
            </a>
          </div>
          <div className="hero-meta"><span><FiMapPin /> India</span><span><FiServer /> Spring Boot · Microservices · AWS</span><span><b>2+ yrs</b> engineering experience</span></div>
        </div>
        <div className="hero-visual reveal delay">
          <div className="portrait-card">
            <div className="portrait-glow" />
            <img src={profile} alt="Vikash Chand" />
            <div className="portrait-overlay" />
            <div className="portrait-label">VIKASH CHAND <small>Backend · Cloud Engineer</small></div>
          </div>
        </div>
        <div className="scroll-cue"><span /> SCROLL TO EXPLORE</div>
      </section>

      <section id="about" className="section-pad section">
        <div className="section-heading"><span>01 / ABOUT</span><h2>A little bit<br /><i>about me.</i></h2></div>
        <div className="about-grid">
          <div className="about-image card">
            <video
              src={aboutVideo}
              autoPlay
              muted
              loop
              playsInline
              preload="auto"
              controls={false}
            />
          </div>
          <div className="about-copy">
            <p className="lead">I enjoy the space where <strong>engineering, product thinking and design</strong> meet.</p>
            <p>My work is increasingly backend and cloud focused: Spring Boot services, microservices, REST APIs and AWS integrations. I still enjoy frontend engineering with React and Angular, especially when it helps me build the complete product end-to-end.</p>
            <p>I also build internal developer tools and extensions that remove repetitive work. My favorite engineering problems are the ones where a measurable performance win is possible.</p>
            <div className="stat-row">
              <div><strong>2+ yrs</strong><span>Professional experience</span></div>
              <div><strong>Backend</strong><span>Cloud-focused engineering</span></div>
              <div><strong>AWS</strong><span>Cloud & microservices</span></div>
            </div>
          </div>
        </div>
      </section>

      <section id="experience" className="section-pad section">
        <div className="section-heading"><span>02 / EXPERIENCE</span><h2>Where I've<br /><i>been building.</i></h2></div>
        <div className="timeline">{timeline.map((item, i) => <article className="timeline-item animate-on-scroll" key={item.year}>
          <div className="timeline-marker">0{i + 1}</div>
          <div className="timeline-date">{item.year}{i === 0 && <small className="live-date">LIVE · {liveDate}<br />{liveTime}</small>}</div>
          <div className="timeline-card card">
            <div className="company-row">
              <a className="company-logo" href={item.companyUrl} target="_blank" rel="noreferrer" aria-label={`${item.company} on LinkedIn`}>
                <img src={item.companyLogo} alt={`${item.company} logo`} />
              </a>
              <div><span className="role">{item.role}</span><h3><a href={item.companyUrl} target="_blank" rel="noreferrer">{item.company}</a></h3></div>
            </div>
            <p>{item.text}</p>
            <div className="experience-tech">{item.tech.map(t => <span className="tech-logo" key={t.name}><img src={t.src} alt="" /><b>{t.name}</b></span>)}</div>
            {item.recommendation && <div className="inline-links"><a
              className="recommendation-link"
              href={recommendationLetter}
              download="Vikash-Chand-Valsco-Recommendation-Letter.pdf"
            >
              Recommendation Letter
              <FiDownload />
            </a></div>}
          </div>
        </article>)}</div>
      </section>

      <section className="section-pad section engineering-section">
        <div className="section-heading"><span>03 / ENGINEERING HIGHLIGHTS</span><h2>Problems I turned<br /><i>into systems.</i></h2></div>
        <div className="engineering-grid">{engineeringHighlights.map((item, i) => {
          const Icon = item.icon; return <article className="engineering-card card animate-on-scroll" key={item.title}>
            <div className="engineering-top"><span className="engineering-index">0{i + 1}</span><Icon /></div>
            <span className="engineering-eyebrow">{item.eyebrow}</span><h3>{item.title}</h3>
            <p>{item.desc}</p>
            <div className="tags">{item.tags.map(t => <span key={t}>{t}</span>)}</div>
          </article>
        })}</div>
      </section>

      <section id="skills" className="section-pad section">
        <div className="section-heading"><span>04 / TOOLKIT</span><h2>Tools I use to<br /><i>make things real.</i></h2></div>
        <div className="skills-layout">
          <div className="skill-intro card"><FiTerminal /><h3>Curious by default.</h3><p>My strongest lane is backend and cloud engineering. I move comfortably across APIs, microservices, databases, frontend integration and AWS infrastructure when the product needs the full stack.</p><div className="skill-pill-row"><span>Build</span><span>Ship</span><span>Learn</span><span>Improve</span></div></div>
          <div className="skill-grid">{skills.map(([name, cat, Icon], i) => <div className="skill-card animate-on-scroll" style={{ "--delay": `${i * 35}ms` }} key={name}><span className="skill-number">{String(i + 1).padStart(2, "0")}</span><span className="skill-icon"><Icon /></span><div><b>{name}</b><small>{cat}</small></div></div>)}</div>
        </div>
      </section>

      <section id="projects" className="section-pad section">
        <div className="section-heading projects-heading"><div><span>05 / SELECTED WORK</span><h2>Things I've<br /><i>shipped.</i></h2></div><a className="text-link" href="https://github.com/vikashchand" target="_blank" rel="noreferrer">View GitHub <FiArrowUpRight /></a></div>
        <div className="project-grid">{projects.map((p, i) => <article className={`project-card card animate-on-scroll ${p.featured ? "featured" : ""}`} key={p.title}>
          <div className="project-image"><img src={p.image} alt={p.title} /><div className="project-number">0{i + 1}</div><a className="project-arrow" href={p.demo || p.github} target="_blank" rel="noreferrer"><FiArrowUpRight /></a></div>
          <div className="project-info"><small>{p.type}</small><h3>{p.title}</h3><p>{p.desc}</p><div className="tags">{p.tags.map(t => <span key={t}>{t}</span>)}</div><div className="project-links">{p.demo && <a href={p.demo} target="_blank" rel="noreferrer">Live <FiExternalLink /></a>}<a href={p.github} target="_blank" rel="noreferrer"><FiGithub /> Code</a></div></div>
        </article>)}</div>
      </section>

      <section id="contact" className="contact-section section-pad">
        <div className="contact-inner">
          <div><span className="eyebrow">06 / CONTACT</span><h2>Have a problem<br />worth <i>solving?</i></h2><p>Let's build something useful, beautiful and a little bit over-engineered.</p></div>
          <div className="contact-actions"><a className="contact-mail" href="mailto:vikashchand147@gmail.com"><span><FiMail /> vikashchand147@gmail.com</span><FiArrowUpRight /></a><a className="contact-mail" href="https://www.linkedin.com/in/vikash-chand28/" target="_blank" rel="noreferrer"><span><FiLinkedin /> LinkedIn</span><FiArrowUpRight /></a><a className="contact-mail" href="https://t.me/Vikash_chand" target="_blank" rel="noreferrer"><span><FaTelegramPlane /> Telegram</span><FiArrowUpRight /></a><a className="contact-mail" href="https://github.com/vikashchand" target="_blank" rel="noreferrer"><span><FiGithub /> GitHub</span><FiArrowUpRight /></a></div>
        </div>
      </section>
    </main>
    <footer><div className="brand"><span>VC</span><b>Vikash<span>.</span></b></div><p>Designed & built with curiosity.</p><div className="footer-links"><a href="https://github.com/vikashchand" target="_blank" rel="noreferrer"><FiGithub /></a><a href="https://www.linkedin.com/in/vikash-chand28/" target="_blank" rel="noreferrer"><FiLinkedin /></a><a href="mailto:vikashchand147@gmail.com"><FiMail /></a></div></footer>
    <button className="back-top" onClick={() => scrollTo("home")} aria-label="Back to top"><FiArrowUpRight /></button>
  </div>
}
export default App;
